import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { endSession, getReportHistory } from '../api';
import { logoutUser } from '../utils/logout';
import BirthDetailsModal from './utils/BirthDetailsModal';
import {
  COUNTRIES,
  RELIGIONS,
  MONTHS
} from '../constants/chatConstants';
import InsufficientCreditsModal from './InsufficientCreditsModal';

const ALL_NAV_ITEMS = [
  { path: '/home', label: 'Home' },
  { path: '/chat', label: 'Chat' },
  { path: '/call', label: 'Appointment' },
  { path: '/reports', label: 'Reports' },
  { path: '/refer', label: 'Refer' },
  { path: '/feedback', label: 'Feedback' },
  { path: '/kundli', label: 'Kundli' },
  { path: '/mahadasha', label: 'Mahadasha' },
  { path: '/moon', label: 'Moon' },
  { path: '/aboutus', label: 'About Us' },
  { path: '/contactus', label: 'Contact Us' },
  { path: '/terms', label: 'Terms' }
];

const PUBLIC_NAV_ITEMS = [
  { path: '/home', label: 'Home' },
  { path: '/aboutus', label: 'About Us' },
  { path: '/contactus', label: 'Contact Us' },
  { path: '/terms', label: 'Terms' }
];

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  
  // ✅ NEW: Notification states
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [latestReport, setLatestReport] = useState(null);
  const [lastCheckedReportId, setLastCheckedReportId] = useState(null);

  const navItems = user ? ALL_NAV_ITEMS : PUBLIC_NAV_ITEMS;

  // ✅ Fetch latest report when user is available
  useEffect(() => {
    if (user?.userId || user?.id) {
      fetchLatestReport();
    }
  }, [user]);

  // ✅ Fetch latest report from API
  const fetchLatestReport = async () => {
    try {
      const userId = user?.userId || user?.id;
      const response = await getReportHistory(userId);
      
      if (response?.data?.success && response.data.reports?.length > 0) {
        const reports = response.data.reports;
        // Sort by date descending to get latest
        const sortedReports = reports.sort((a, b) => 
          new Date(b.generated_at || b.createdAt) - new Date(a.generated_at || a.createdAt)
        );
        const latest = sortedReports[0];
        
        // Check if this is a new report (different from last checked)
        const reportId = latest.id || latest.reportId;
        const storedLastReportId = localStorage.getItem('lastCheckedReportId');
        
        if (storedLastReportId !== String(reportId)) {
          setLatestReport(latest);
          setLastCheckedReportId(reportId);
        }
      }
    } catch (error) {
      console.error('Error fetching report history:', error);
    }
  };

  // ✅ Check if report is less than 6 days old
  const isReportWithin6Days = (timestamp) => {
    if (!timestamp) return false;
    const reportDate = new Date(timestamp);
    const now = new Date();
    const diffMs = now - reportDate;
    const SIX_DAYS_IN_MS = 6 * 24 * 60 * 60 * 1000; // 518,400,000 ms
    return diffMs < SIX_DAYS_IN_MS;
  };

  // ✅ Build notifications array based on conditions
  useEffect(() => {
    const newNotifications = [];

    // 1. Low Credits Notification (credits < 10)
    if (user && user.credits < 10) {
      newNotifications.push({
        id: 'low-credits',
        type: 'warning',
        title: 'Low Credits Alert',
        message: `You have only ${user.credits} credits remaining. Please recharge to continue using our services.`,
        time: 'Now',
        read: false,
        action: '/credits'
      });
    }

    // 2. Latest Report Notification (Only if report is less than 6 days old)
    if (latestReport && lastCheckedReportId) {
      const storedLastReportId = localStorage.getItem('lastCheckedReportId');
      const reportId = latestReport.id || latestReport.reportId;
      const reportTimestamp = latestReport.generated_at || latestReport.createdAt;
      
      // Only show if this is a new report AND report is within 6 days
      if (storedLastReportId !== String(reportId) && isReportWithin6Days(reportTimestamp)) {
        const reportType = latestReport.report_type || latestReport.reportType || 'Report';
        newNotifications.push({
          id: `report-${reportId}`,
          type: 'success',
          title: 'New Report Generated',
          message: `Your ${reportType} report has been successfully generated. Click to view.`,
          time: formatReportTime(reportTimestamp),
          read: false,
          action: '/reports'
        });
      }
    }

    setNotifications(newNotifications);
  }, [user, latestReport, lastCheckedReportId]);

  // ✅ Format report time
  const formatReportTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // ✅ Calculate unread notifications count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Listen for storage changes (logout from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'astroguru_user' && !e.newValue) window.location.reload();
    };
    const handleUserLoggedOut = () => {
      setShowUserMenu(false);
      window.location.href = '/';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedOut', handleUserLoggedOut);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedOut', handleUserLoggedOut);
    };
  }, []);

  // Detect mobile app and apply safe area
  useEffect(() => {
    const isMobileApp = () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      window.ReactNativeWebView ||
      /wv|WebView/i.test(navigator.userAgent || navigator.vendor || window.opera);

    if (isMobileApp()) document.documentElement.classList.add('mobile-app-mode');
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu-container')) setShowUserMenu(false);
      if (!event.target.closest('.notification-container')) setShowNotifications(false);
      if (!event.target.closest('.nav-dropdown-mobile')) {
        setShowNavMenu(false);
        document.body.classList.remove('mobile-menu-open');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Logout handler using external function
  const handleLogout = useCallback(() => {
    logoutUser({
      user,
      logout,
      endSession,
      setIsLoggingOut,
      setShowUserMenu
    });
  }, [logout, user]);

  // Close mobile nav and remove body class
  const handleMobileMenuClick = () => {
    setShowNavMenu(false);
    document.body.classList.remove('mobile-menu-open');
  };

  const handleProfileClick = () => {
    setShowProfileEditModal(true);
    setShowUserMenu(false);
  };

  // ✅ Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications(notifications.map(n => 
      n.id === notification.id ? { ...n, read: true } : n
    ));

    // Store last checked report ID if it's a report notification
    if (notification.id.startsWith('report-')) {
      localStorage.setItem('lastCheckedReportId', String(lastCheckedReportId));
    }

    // Navigate to the action page
    if (notification.action) {
      setShowNotifications(false);
      navigate(notification.action);
    }
  };

  // ✅ Mark all as read
  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    
    // Store the latest report ID as checked
    if (lastCheckedReportId) {
      localStorage.setItem('lastCheckedReportId', String(lastCheckedReportId));
    }
  };

  return (
    <>
      {isLoggingOut && (
        <div className="logout-overlay">
          <div className="logout-message">
            <div className="logout-spinner"></div>
            <p>Logging out securely...</p>
          </div>
        </div>
      )}

      {showInsufficientCreditsModal && (
        <InsufficientCreditsModal
          onClose={() => setShowInsufficientCreditsModal(false)}
          onPurchase={() => {
            setShowInsufficientCreditsModal(false);
            window.location.href = '/credits';
          }}
        />
      )}

      <header className="app-header">
        {/* Header Left: Logo */}
        <div className="header-left">
          <Link to="/home" className="logo-section">
            <span className="logo"></span>
            <h1 className="logo-text">AastroG</h1>
          </Link>
        </div>

        {/* Centered Navigation */}
        <div className="nav-container-center">
          {/* Desktop Horizontal Grid */}
          <div className="nav-grid-desktop">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-grid-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <span className="nav-label">{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Mobile: Dropdown Navigation */}
          <div className="nav-dropdown-mobile">
            <button
              className="nav-toggle"
              onClick={() => {
                setShowNavMenu((prev) => !prev);
                document.body.classList.toggle('mobile-menu-open');
              }}
            >
              <span className="current-page">☰</span>
            </button>
            {showNavMenu && (
              <div className="nav-menu show">
                <Link to="/home" className="menu-logo-section" onClick={handleMobileMenuClick}>
                  <span className="logo"></span>
                  <h1 className="logo-text">AastroG</h1>
                </Link>
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={handleMobileMenuClick}
                  >
                    <span className="nav-label">{item.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile center logo */}
        <div className="mobile-center-logo">
          <Link to="/home" className="center-logo-link">
            <span className="logo"></span>
            <h1 className="logo-text">AastroG</h1>
          </Link>
        </div>

        {/* Header Right: Notifications + User Menu */}
        <div className="header-right">
          {user && (
            <>
              {/* ✅ NOTIFICATION BELL ICON - Only show if there are notifications */}
              {notifications.length > 0 && (
                <div className="notification-container">
                  <div
                    className="notification-bell"
                    onClick={() => setShowNotifications((prev) => !prev)}
                  >
                    <span className="bell-icon">🔔</span>
                    {unreadCount > 0 && (
                      <span className="notification-badge">{unreadCount}</span>
                    )}
                  </div>

                  {/* ✅ NOTIFICATION DROPDOWN */}
                  {showNotifications && (
                    <div className="notification-dropdown">
                      <div className="notification-header">
                        <h4>Notifications</h4>
                        {unreadCount > 0 && (
                          <button 
                            className="mark-all-read-btn"
                            onClick={markAllAsRead}
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>
                      
                      <div className="notification-list">
                        {notifications.length === 0 ? (
                          <div className="no-notifications">
                            <span>🔕</span>
                            <p>No new notifications</p>
                          </div>
                        ) : (
                          notifications.map((notif) => (
                            <div
                              key={notif.id}
                              className={`notification-item ${notif.read ? 'read' : 'unread'}`}
                              onClick={() => handleNotificationClick(notif)}
                            >
                              <div className="notification-icon">
                                {notif.type === 'success' && '✅'}
                                {notif.type === 'info' && 'ℹ️'}
                                {notif.type === 'warning' && '⚠️'}
                              </div>
                              <div className="notification-content">
                                <div className="notification-title">{notif.title}</div>
                                <div className="notification-message">{notif.message}</div>
                                <div className="notification-time">{notif.time}</div>
                              </div>
                              {!notif.read && <div className="unread-dot"></div>}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* USER MENU */}
              <div className="user-menu-container">
                <div
                  className="user-info"
                  onClick={() => setShowUserMenu((prev) => !prev)}
                >
                  <div className="user-avatar">
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt="Profile"
                        className="avatar-img"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="avatar-initials">
                        {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <span className="user-name">
                    {user.full_name?.split(' ')[0] || 'User'}
                  </span>
                </div>
                
                {showUserMenu && (
                  <div className="user-dropdown-menu">
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        {user.profile_picture ? (
                          <img
                            src={user.profile_picture}
                            alt="Profile"
                            className="dropdown-avatar-img"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <span className="dropdown-avatar-placeholder">
                            {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        )}
                      </div>
                      <div className="dropdown-user-info">
                        <div className="dropdown-user-name">
                          {user.full_name || 'User'}
                        </div>
                        <div className="dropdown-user-email">
                          {user.email || 'user@example.com'}
                        </div>
                      </div>
                    </div>

                    {/* Credits Display Inside Dropdown */}
                    <Link to="/credits" className="dropdown-item dropdown-credits-item" onClick={() => setShowUserMenu(false)}>
                      <span className="dropdown-icon dimond">💎</span>
                      <span className="dropdown-credits-info">
                        <span className="dropdown-credits-label">Credits: </span>
                        <span className={`dropdown-credits-count ${user.credits < 10 ? 'low-credits' : ''}`}>
                          {user.credits || 0}
                        </span>
                      </span>
                      {user.credits < 10 && <span className="warning-icon">⚠️</span>}
                    </Link>

                    <div className="dropdown-divider"></div>

                    <button
                      className="dropdown-item profile-modal-trigger"
                      onClick={handleProfileClick}
                    >
                      <span className="dropdown-icon">👤</span>
                      Profile
                    </button>
                    
                    <div className="dropdown-divider"></div>
                    
                    <button
                      className="dropdown-item logout-item"
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                    >
                      <span className="dropdown-icon">
                        {isLoggingOut ? '⏳' : '🚪'}
                      </span>
                      {isLoggingOut ? 'Logging out...' : 'Logout'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {showProfileEditModal && (
        <BirthDetailsModal
          show={showProfileEditModal}
          close={() => setShowProfileEditModal(false)}
          isEdit={true}
          isFromAuth={false}
          birthDetails={{
            full_name: user?.full_name || '',
            religion: user?.religion || 'Hindu',
            birth_day: user?.birth_date ? new Date(user.birth_date).getDate().toString() : '',
            birth_month: user?.birth_date ? (new Date(user.birth_date).getMonth() + 1).toString() : '',
            birth_year: user?.birth_date ? new Date(user.birth_date).getFullYear().toString() : '',
            birth_time: user?.birth_time || '',
            birth_place: user?.birth_place || '',
            country_code_no: user?.country_code_no || '+91',
            mobile_number: user?.mobile_number || '',
            country_code: user?.country_code || 'IN'
          }}
          COUNTRIES={COUNTRIES}
          RELIGIONS={RELIGIONS}
          MONTHS={MONTHS}
        />
      )}
    </>
  );
}

export default Header;
